import { Badge, Button, useMediaQuery } from "@mui/material";
import { FormikProps } from "formik";
import { usePaymentActions } from "hooks/usePaymentActions";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, Drawer, IconButton } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { BubbleChatIcon } from "@hugeicons-pro/core-stroke-rounded";
import PaymentDetailsSummary from "./PaymentDetailsSummary";
import { LocalImageAsset } from "hooks/useAttachmentAndAssetHandling";
import {
  canEditPayment,
  canResubmitPayment,
  FormikPayment,
} from "utils/payments/paymentFormat";
import { useEffect, useState } from "react";
import { EditPendingPaymentModal } from "./PaymentModules";
import PaymentStatus from "enums/PaymentStatus";
import PaymentDrawerRouter from "./PaymentDrawerRouter";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import CommentsPanel from "components/common/comments/CommentsPanel";
import { useComments } from "hooks/useComments";
import { ParentType } from "enums/Comments";

// The DS drawer's own default width. Mirrored here only because the comments
// panel docks flush against the paper edge and has to know where it is.
const DRAWER_WIDTH = 420;

interface PaymentDetailsDrawerProps {
  open: boolean;
  paymentId: string;
  handleClose: () => void;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

const ViewPaymentDetailsDrawer = (props: PaymentDetailsDrawerProps) => {
  const { open, paymentId, handleClose, onAfterClose } = props;
  const { t } = useTranslation();

  const [openEditPendingPaymentModal, setOpenEditPendingPaymentModal] =
    useState(false);
  const [openPaymentRouter, setOpenPaymentRouter] = useState(false);
  const [unsavedChangesModal, setUnsavedChangesModal] = useState(false);
  const [customFieldsModified, setCustomFieldsModified] = useState(false);
  const [expenseCategoryModified, setExpenseCategoryModified] = useState(false);
  const [allocationModified, setAllocationModified] = useState(false);

  const {
    formikPayment,
    customFieldSettings,
    workflowRequest,
    specificLoadingStates,
    approvePayment,
    rejectPayment,
    refetchPayment,
    handleSaveClassification,
  } = usePaymentActions({
    paymentId,
    shouldFetchPayments: open,
  });

  const paymentIdToUse = paymentId || formikPayment.values.id;

  // The panel docks beside the drawer only when there is room for both.
  const commentsDockable = useMediaQuery(`(min-width:${DRAWER_WIDTH + 520}px)`);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [nestedComments, setNestedComments] = useState(false);
  const showComments =
    open && commentsOpen && !openPaymentRouter && !nestedComments;

  const commentActions = useComments(
    open ? paymentIdToUse : "",
    ParentType.PAYMENT,
  );
  const commentCount = commentActions.comments?.length ?? 0;

  // OUT-1557: this was `isAdminOrCreator` via useCheckUserRoleWithSelf, which
  // compared the auth-provider id to a backend primary key and so never matched
  // the creator. Named for what it actually does until that is restored.
  const isAdminOrCreator = useCheckUserRole(Role.Admin);

  const handleLocalFileUpload = (files: LocalImageAsset[]) => {
    const newAttachments = files.map((file) => ({
      id: file?.id,
      assetUrl: file?.assetUrl,
      extension: file?.extension,
      file: file?.file,
    }));

    formikPayment.setFieldValue("attachments", [
      ...formikPayment.values.attachments,
      ...newAttachments,
    ]);
  };

  const handleRemoveAsset = (assetId: string) => {
    formikPayment.setFieldValue(
      "attachments",
      formikPayment.values.attachments.filter((asset) => asset.id !== assetId),
    );
  };

  const onApprove = async () => {
    await approvePayment({
      variables: {
        requestId: workflowRequest?.id,
      },
    });
    refetchPayment && refetchPayment();
  };

  const onReject = async () => {
    await rejectPayment({
      variables: {
        requestId: workflowRequest?.id,
      },
    });
    refetchPayment && refetchPayment();
  };

  const handleCloseWithCheck = (bypassCheck: boolean = false) => {
    if (
      !bypassCheck &&
      (customFieldsModified || expenseCategoryModified || allocationModified) &&
      isAdminOrCreator
    ) {
      setUnsavedChangesModal(true);
    } else {
      setCustomFieldsModified(false);
      setExpenseCategoryModified(false);
      setAllocationModified(false);
      handleClose();
    }
  };

  const showEditPayment =
    isAdminOrCreator &&
    (canEditPayment(formikPayment.values.status as PaymentStatus) ||
      canResubmitPayment(
        formikPayment.values.status as PaymentStatus,
        formikPayment.values.actualPaymentDate,
      ));

  const isDetailsDirty =
    customFieldsModified || expenseCategoryModified || allocationModified;

  const handleSaveDetails = async () => {
    await handleSaveClassification();
    setCustomFieldsModified(false);
    setExpenseCategoryModified(false);
    setAllocationModified(false);
    refetchPayment && refetchPayment();
  };

  useEffect(() => {
    const customFieldsDirty = formikPayment.dirty;
    setCustomFieldsModified(!!customFieldsDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formikPayment.values.customFields]);

  const busy =
    specificLoadingStates.savingPayment ||
    specificLoadingStates.approveLoading ||
    specificLoadingStates.rejectLoading;

  return (
    <>
      <Drawer
        open={open}
        onClose={() => handleCloseWithCheck()}
        onAfterClose={() => {
          setCommentsOpen(false);
          onAfterClose?.();
        }}
        title={t("Payment Details")}
        loading={busy}
        disableEnforceFocus={showComments}
        headerAction={
          <IconButton
            variant="ghost"
            aria-label={t("Comments")}
            onClick={() => setCommentsOpen((wasOpen) => !wasOpen)}
          >
            <Badge badgeContent={commentCount} color="primary">
              <HugeiconsIcon icon={BubbleChatIcon} size={18} />
            </Badge>
          </IconButton>
        }
        footer={
          <>
            <Button
              variant="primary-outlined"
              onClick={() => handleCloseWithCheck()}
            >
              {t("Close")}
            </Button>

            {isAdminOrCreator && (
              <Button
                variant="primary-filled"
                disabled={
                  !isDetailsDirty || specificLoadingStates.savingClassification
                }
                onClick={handleSaveDetails}
              >
                {t("Save")}
              </Button>
            )}

            {showEditPayment && (
              <Button
                variant="primary-filled"
                onClick={() => {
                  if (
                    formikPayment.values.status ===
                    PaymentStatus.PENDING_PAYMENT
                  ) {
                    setOpenEditPendingPaymentModal(true);
                  } else {
                    setOpenPaymentRouter(true);
                  }
                }}
              >
                {t("Review")}
              </Button>
            )}
          </>
        }
      >
        {!openPaymentRouter && (
          <PaymentDetailsSummary
            formikPayment={formikPayment as FormikProps<FormikPayment>}
            customFieldSettings={customFieldSettings}
            specificLoadingStates={specificLoadingStates}
            workflowRequest={workflowRequest}
            setExpenseCategoryModified={setExpenseCategoryModified}
            setAllocationModified={setAllocationModified}
            handleLocalFileUpload={handleLocalFileUpload}
            handleRemoveAttachments={handleRemoveAsset}
            onNestedCommentsChange={setNestedComments}
            refetchPayment={refetchPayment}
            onApprove={onApprove}
            onReject={onReject}
          />
        )}
      </Drawer>

      <CommentsPanel
        open={showComments}
        onClose={() => setCommentsOpen(false)}
        drawerWidth={DRAWER_WIDTH}
        docked={commentsDockable}
        commentActions={commentActions}
      />

      <EditPendingPaymentModal
        open={openEditPendingPaymentModal}
        setOpen={setOpenEditPendingPaymentModal}
        handleYes={() => {
          setOpenPaymentRouter(true);
          setOpenEditPendingPaymentModal(false);
        }}
      />

      <PaymentDrawerRouter
        open={openPaymentRouter}
        paymentId={paymentIdToUse}
        handleClosePaymentDrawerRouter={(refresh) => {
          setOpenPaymentRouter(false);
          if (refresh) {
            refetchPayment();
          }
        }}
      />

      <ConfirmDialog
        open={unsavedChangesModal}
        tone="primary"
        title={t("Unsaved Changes")}
        message={t("UnsavedChangesDescription")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          handleCloseWithCheck(true);
          setUnsavedChangesModal(false);
        }}
        onCancel={() => setUnsavedChangesModal(false)}
      />
    </>
  );
};

export default ViewPaymentDetailsDrawer;
